import { RefObject } from "react";
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, HeadingLevel, AlignmentType, WidthType, BorderStyle, ShadingType, ImageRun, VerticalAlign, TabStopType } from "docx";
import { calculateFinancialRatios, calculateLLPFinancialRatios } from "../../utils/financialRatios";
import { getDefaultRecords } from "../../utils/financialTop3consolidate";
import { GstSession, ShareHoldingMoreThenFivePercentData } from "../../types/CustomerInterface";
import Colour_Astrafin from "../../assets/Colour-Astrafin.png";

export const useDocxGenerator = () => {
  const formatCurrency = (num: any) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    return `₹${Number(num)?.toLocaleString("en-IN", { maximumFractionDigits: 2 })} Cr`;
  };

  const formatNumber = (num: any, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return "N/A";
    return Number(num).toFixed(decimals);
  };

  const fetchImageBuffer = async (src: string): Promise<Uint8Array> => {
    const res = await fetch(src);
    const arrayBuffer = await res.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  };

  // const formatDate = (d?: string) => {
  //   if (!d) return "N/A";
  //   const date = new Date(d);
  //   return date.toLocaleDateString("en-IN", {
  //     day: "2-digit",
  //     month: "short",
  //     year: "numeric",
  //   });
  // };

  const formatDatein = (d?: string) => {
    if (!d) return "N/A";
  
    const date = new Date(d);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const year = date.getFullYear();
  
    return `${day}/${month}/${year}`;
  };

  const logoUrl = "/assets/Colour-Astrafin.png";

  const getLabel = (key: string): string => {
    const specialMappings: Record<string, string> = {
      ebitda: "EBITDA",
      ebitdaPercent: "EBITDA (%)",
      pat: "PAT", // add more mappings as needed
      patPercent: "PAT (%)", 
      dscr: "DSCR",
      cogs:"COGS",
      tolTnwRatio:"TOL TNW RATIO",
      nonCurrentAssetsExclFixed:"Non Current Assets (Excl Fixed Assets)",
      revenueGrowth:"Revenue Growth (%)"
    };
  
    // Check for exact match (case insensitive)
    const normalizedKey = key.toLowerCase();
    const mappedEntry = Object.entries(specialMappings).find(
      ([k]) => k.toLowerCase() === normalizedKey
    );
    if (mappedEntry) return mappedEntry[1];
  
    // Already all uppercase? Return as-is
    if (key === key.toUpperCase()) {
      return key;
    }
  
    // Default formatting (split camelCase & capitalize first word)
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, str => str.toUpperCase());
  };

  const generateDocx = async (data: any,shareDebtProfile:any,selectedDate?:Date) => {


    const logoBuffer = await fetchImageBuffer(Colour_Astrafin);

    const formatFinancialYear = (dateStr: string) => {
      const year = new Date(dateStr).getFullYear();
      const fyStart = year - 1;
      const fyEnd = year.toString().slice(-2);
      return `FY ${fyStart} - ${fyEnd}`;
    };

   const formattedDate = selectedDate
  ? selectedDate.toLocaleDateString("en-GB") // dd/mm/yyyy
  : "N/A";

  const formattedDateshareholding = data.company_details.last_updated_date || null;

    const logoImage = new Paragraph({
      children: [
        new ImageRun({
          data: logoBuffer,
          transformation: {
            width: 180,
            height: 90,
          },
          // Fix: explicitly set type for PNG images to resolve lint error
          // See: https://docx.js.org/#/usage/images?id=svg-support
          // If Colour_Astrafin is a PNG, set type: "png"
          // If it's SVG, set type: "svg"
          type: "png",
        }),
      ],
      alignment: AlignmentType.CENTER,
    });

    const totalAmount = data.openChargesSession?.reduce((sum: any, charge: any) => sum + (charge?.amount ?? 0), 0) ?? 0;
    const companyDetails = [
      { label: "Legal Entity Name", value: data?.company_details?.legal_name ?? "-" },
      { label: "Brand Name", value: data?.company_details?.brand ?? "-" },
      { label: "Website", value: data?.company_details?.website ?? "-" },
      { label: "Entity Type", value: data?.company_details?.entity_type ?? "-" },
      { label: "Share Price", value: "-" },
      { label: "Market Cap", value: "-" },
      { label: "Group Company", value: "-" },
      { label: "Proposed Facility", value: "-" },
      { label: "Proposed Quantum", value: "-" },
      { label: "Proposed Collateral", value: "-" },
      { label: "PAN", value: data?.company_details?.pan ?? "-" },
      { label: "CIN/LLPIN", value: data?.company_details?.cin ?? "-" },
      {
        label: "City, State",
        value:
          data?.company_details?.city && data?.company_details?.state
            ? `${data.company_details.city}, ${data.company_details.state}`
            : data?.company_details?.city ?? data?.company_details?.state ?? "-"
      },
      { label: "Registered Address", value: data?.company_details?.registered_address ?? "-" },
      {
        label: "Incorporation Date",
        value: data?.company_details?.incorporation_date
          ? formatDatein(data.company_details.incorporation_date)
          : "-"
      },
      {
        label: "Turnover",
        value:
          data?.company_details?.turnoverYear && data?.company_details?.turnoverValue
            ? `${data.company_details.turnoverYear}, ${data.company_details.turnoverValue}`
            : "-"
      },
      {
        label: "Networth",
        value:
          data?.company_details?.networthYear && data?.company_details?.networthValue
            ? `${data.company_details.networthYear}, ${data.company_details.networthValue}`
            : "-"
      },
      { label: "Credit Rating", value: data?.company_details?.creditRating ?? "-" },
      { label: "Credit Rating Agency", value: data?.company_details?.creditRatingAgency ?? "-" }
    ];

    const businessDetails_row = [
      {
        label: "Industry",
        value: data?.businessSession?.industry
      },
      {
        label: "About Company",
        value: data?.businessSession?.about_company
      },
      {
        label: "Business Model",
        value: data?.businessSession?.business_model
      }
    ];

    const business_details_table_rows = businessDetails_row.map(({ label, value }) =>
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: label || "N/A",
                    bold: true,
                    size : 20,
                    font: "Montserrat",
                  }),
                ],
              }),
            ],
            width: { size: 35, type: WidthType.PERCENTAGE },
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
    
          new TableCell({
            children: [
              new Paragraph({
                children: (value || "N/A")
                  .split("\n")
                  .flatMap((line:any, index:any, arr:any) => {
                    const runs = [
                      new TextRun({
                        text: line,
                        size : 20,
                        font: "Montserrat",
                      }),
                    ];
                    if (index < arr.length - 1) {
                      runs.push(new TextRun({ break: 1 }));
                    }
                    return runs;
                  }),
              }),
            ],
            width: { size: 65, type: WidthType.PERCENTAGE },
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
        ],
      })
    );

    const company_details_row = companyDetails.map(({ label, value }) =>
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: label || "N/A", bold: true, size : 20 })],
              }),
            ],
            width: { size: 35, type: WidthType.PERCENTAGE },
             shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: value || "N/A",
                    size : 20,
                    color: label === "Status" ? "059669" : undefined,
                  }),
                ],
              }),
            ],
            width: { size: 65, type: WidthType.PERCENTAGE },
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
        ],
      })
    );

    const formatCurrency = (num: number = 0): string =>
      `${num?.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

    const openChargesTableRows = data.openChargesSession?.map((charge: any) =>
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: charge.holder_name || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatDatein(charge.date)  || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatCurrency(charge.amount) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: charge.type || "N/A", size : 20, color: "065F46" })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
        ],
      })
    ) || [];

    const openChargesTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Charge Holder", bold: true, size : 20 })]
              })],
              width: { size: 40, type: WidthType.PERCENTAGE },
              shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Date", bold: true, size : 20 })]
              })],
              width: { size: 20, type: WidthType.PERCENTAGE },
              shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Amount", bold: true, size : 20 })]
              })],
              width: { size: 25, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Status", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
          ],
        }),
        ...openChargesTableRows,
      ],
    });

    const directorTableRows = data.directorSession?.map((director: any) =>
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: director.name || "-", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: director.din || "-", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: director.designation || "-", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: director.tenure || "-", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
        ],
      })
    );

    const directorTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "Name", bold: true, size : 20 })] })],
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "DIN", bold: true, size : 20 })] })],
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "Designation", bold: true, size : 20 })] })],
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: "Tenure", bold: true, size : 20 })] })],
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
          ],
        }),
        ...directorTableRows,
      ],
    });

    const isLLPIN = data.financialSession.some((f: any) => 
      (f as any).isLLPIN || 
      (f as any).statement_of_assets_and_liabilities || 
      (f as any).statement_of_income_and_expenditure
    );

    const ratios = isLLPIN ? calculateLLPFinancialRatios(getDefaultRecords(data.financialSession)) : calculateFinancialRatios(getDefaultRecords(data.financialSession));
    const keyRatiosTables = createKeyRatiosTables(ratios);

    function createKeyRatiosTables(ratios: any[]) {
      const sections = [
        {
          title: "Revenue & Profitability (Figures are represented in ₹ Cr)",
          metrics: [
            "netRevenue", "revenueGrowth", "otherIncome", "cogs",
            "employeeBenefitExpenses", "otherExpenses", "ebitda",
            "ebitdaPercent", "financeCost", "depreciation", "pat", "patPercent"
          ]
        },
        {
          title: "Assets & Capital Structure (Figures are represented in ₹ Cr)",
          metrics: [
            "tangibleAssets", "intangibleAssets", "capitalWIP",
            "nonCurrentAssetsExclFixed", "tangibleNetWorth", 
            "totalOutsideLiabilities", "tolTnwRatio"
          ]
        },
        {
          title: "Liquidity & Working Capital (Figures are represented in ₹ Cr)",
          metrics: [
            "longTermDebt", "shortTermDebt", "totalDebt", "currentAssets",
            "currentLiabilities", "currentRatio", "netWorkingCapital", "inventories",
            "tradeReceivables", "tradePayables", "cashAndCashEquivalents",
            "inventoriesHoldingPeriod", "debtorsHoldingPeriod",
            "creditorsHoldingPeriod", "drawingPower", "debtEquityRatio", "dscr"
          ]
        }
      ];

      return sections.flatMap(section => [
        new Paragraph({
          children: [
            new TextRun({
              text: section.title || "N/A",
              bold: true,
              size: 28,
              color: "000000"
            })
          ],
          spacing: { before: 600, after: 200 }
        }),
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
          },
          rows: [
            // Header Row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Metric" , bold: true, size : 20 })]
                  })],
                   shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  width: { size: 30, type: WidthType.PERCENTAGE },
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                ...ratios.map(r => new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: formatFinancialYear(r.year) || "N/A", bold: true, size : 20 })]
                  })],
                   shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  width: { size: 70 / ratios.length, type: WidthType.PERCENTAGE },
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }))
              ]
            }),

            // Metric Rows
            ...section.metrics.map(metric =>
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: getLabel(metric) || "N/A",bold:true, size : 20,  })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  ...ratios.map(r => {
                    let value = "N/A";
                    if (typeof r[metric] === "number") {
                      if (metric.includes("Percent") || (/\(\s*%\s*\)/.test(metric)) ) {
                        value = formatNumber(r[metric]) + "%";
                      } else if (metric.includes("Ratio") || metric.toLowerCase().includes("dscr") ) {
                        value = formatNumber(r[metric]) + "x";
                      } else if (metric.toLowerCase().includes("revenuegrowth") ){
                        if(r[metric] == 0 ){
                          value = "N/A"
                        }else{
                          value = formatNumber(r[metric]) + "%"
                        }
                      }else {
                        value = formatCurrency(r[metric]);
                      }
                    }else if(typeof r[metric] === "string"){
                      if(r[metric] == '0' ){
                        value = "N/A"
                      }else{
                        value = r[metric]
                      }
                    }
                    return new TableCell({
                      children: [new Paragraph({alignment: AlignmentType.CENTER, children: [new TextRun({ text: value || "N/A", size : 20 })] })],
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                      
                    });
                  })
                ]
              })
            )
          ]
        })
      ]);
    }

    function createGstDetailsTable(gstDetails: GstSession[]) {
      return [
        // Section Title
        new Paragraph({
          children: [
            new TextRun({
              text: "Key GST Details",
              bold: true,
              size: 28,
              color: "000000", // Tailwind's orange-900
            }),
          ],
          spacing: { before: 600, after: 200 },
        }),

        // Table
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" }, // light gray
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            left: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            right: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
          },
          rows: [
            // Header Row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "GSTIN", bold: true, size : 20 })],
                  })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"}, // gray-50
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "State", bold: true, size : 20 })],
                  })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Registration Date", bold: true, size : 20 })],
                  })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
              ],
            }),

            // Data Rows
            ...(gstDetails ?? []).map((gst) =>
              new TableRow({
                children: [

                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: gst.gstin || "N/A", size : 20 })] })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),


                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: gst.state || "N/A", size : 20 })] })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({ children: [new TextRun({ text: formatDatein(gst.registration) || "N/A", size : 20 })] })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                ],
              })
            )
          ]
        }),
      ];
    }



    const gstDetailsSection = createGstDetailsTable(data.gstSession);

    const totalLoanAmount = shareDebtProfile.reduce((sum: number, row: any) => sum + (row.totalLoanAmount || 0), 0);
const totalEmi = shareDebtProfile.reduce((sum: number, row: any) => sum + (row.emi || 0), 0);
const totalOutstanding = shareDebtProfile.reduce((sum: number, row: any) => sum + (row.outstandingAsOnDate || 0), 0);

    const debtProfileTableRows = shareDebtProfile?.map((debt: any) =>
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: debt.nature || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: debt.bankName || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: debt.loanType || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(debt.totalLoanAmount || "N/A"), size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(debt.emi) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }), new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatDatein(debt.loanStartDate) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }), new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(debt.roi) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }), new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(debt.outstandingAsOnDate) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }), new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(debt.totalTenure) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }), 
        ],
      })
    ) || [];

    const formatIN = (value: number | string) =>
      new Intl.NumberFormat("en-IN").format(typeof value === "number" ? value : parseFloat(value));

    debtProfileTableRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: "Total", bold: true, size: 20 })] })],
            columnSpan: 3,
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatIN(totalLoanAmount), bold: true, size: 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatIN(totalEmi), bold: true, size: 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({ children: [new Paragraph("")] }), // Loan Start Date
          new TableCell({ children: [new Paragraph("")] }), // ROI
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatIN(totalOutstanding), bold: true, size: 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({ children: [new Paragraph("")] }), // Total Tenure
        ],
      })
    );


    const debtProfileTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Nature", bold: true, size : 20 })]
              })],
              width: { size: 40, type: WidthType.PERCENTAGE },
              shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Lender Name", bold: true, size : 20 })]
              })],
              width: { size: 20, type: WidthType.PERCENTAGE },
              shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Loan Type", bold: true, size : 20 })]
              })],
              width: { size: 25, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Total Loan Amount", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),

            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "EMI", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Loan Start Date", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "ROI (%)", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Outstanding", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Total Tenure", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
          ],
        }),
        ...debtProfileTableRows,
      ],
    });


    const securitiesallotmentTableRows = data.securities_allotment?.map((securities_allotment: any) =>
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: formatDatein(securities_allotment.allotment_date) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: securities_allotment.allotment_type || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: securities_allotment.instrument || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(securities_allotment.total_amount_raised || "N/A"), size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: String(securities_allotment.number_of_securities_allotted) || "N/A", size : 20 })] })],
            margins: { top: 150, bottom: 150, left: 150, right: 150 },
          }),
        ],
      })
    ) || [];


    const securitiesallotmentTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
        insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Allotment Date", bold: true, size : 20 })]
              })],
              width: { size: 40, type: WidthType.PERCENTAGE },
              shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Allotment Type", bold: true, size : 20 })]
              })],
              width: { size: 20, type: WidthType.PERCENTAGE },
              shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Instrument", bold: true, size : 20 })]
              })],
              width: { size: 25, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "Amount", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),

            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: "No. of Securities", bold: true, size : 20 })]
              })],
              width: { size: 15, type: WidthType.PERCENTAGE },
               shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
              margins: { top: 150, bottom: 150, left: 150, right: 150 },
            }),
          ],
        }),
        ...securitiesallotmentTableRows,
      ],
    });

    const headerSecuritiesAllotmentParagraph = new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 600, after: 200 },
      children: [
        new TextRun({
          text: "Securities Allotment",
          bold: true,
          size: 28,
          font: "Montserrat",
        }),
        // new TextRun({
        //   text: `  As on ${formattedDateshareholding}`, // two spaces for visual separation
        //   size: 24,
        //   font: "Montserrat",
        // }),
      ],
    });

    const headerRelatedPartyTranstionParagraph = new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 600, after: 200 },
      children: [
        new TextRun({
          text: "Related Party Transactions",
          bold: true,
          size: 28,
          font: "Montserrat",
        })
      ],
    });

    const headerRelatedPartyParagraph = new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 600, after: 200 },
      children: [
        new TextRun({
          text: "Related Party Details",
          bold: true,
          size: 28,
          font: "Montserrat",
        })
      ],
    });

    function createShareholdingSection(
      data: ShareHoldingMoreThenFivePercentData[]
    ): (Paragraph | Table)[] {
      const section: (Paragraph | Table)[] = [];
    
      // Check if data is empty or has no actual shareholding data
      if (!data || data.length === 0) {
        return [];
      }

      // Check if any year block has actual shareholding data
      const hasData = data.some(yearBlock => {
        const types = ["company", "llp", "individual", "others"] as const;
        return types.some(type => (yearBlock[type]?.length || 0) > 0);
      });

      // Section Heading (show once)
      section.push(headershareholdingParagraph);

      // If no data exists, show "no data" message
      if (!hasData) {
        section.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 200 },
            children: [
              new TextRun({
                text: "No shareholding above 5% found for this company.",
                size: 20,
              }),
            ],
          })
        );
        return section;
      }
    
      // Iterate over each year block
      for (const yearBlock of data) {
        const rows: TableRow[] = [];
    
        // Header Row
        rows.push(
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: "Shareholder Name", bold: true, size : 20 })],
                })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"}, // gray-100
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: "Type", bold: true, size : 20 })],
                })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: "Shareholding %", bold: true, size : 20 })],
                })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: "No. of Shares", bold: true, size : 20 })],
                })],
                 shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
            ],
          })
        );
    
        // Helper to render a row
        const renderRow = (name: string, type: string, percent: number, shares: number) =>
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: name || "N/A", size : 20 })] })],
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: type || "N/A", size : 20 })] })],
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: (percent.toFixed(2) + "%") || "N/A", size : 20 })] })],
                margins: { top: 150, bottom: 150, left: 150, right: 150 }
              }),
              new TableCell({
                children: [new Paragraph({ children: [new TextRun({ text: shares?.toLocaleString() || "N/A", size : 20 })] })],
                margins: { top: 150, bottom: 150, left: 150, right: 150 },
              }),
            ],
          });
    
        // Grouped types
        const types = ["company", "llp", "individual", "others"] as const;
    
        for (const type of types) {
          for (const holder of yearBlock[type] ?? []) {
            rows.push(renderRow(holder.name, type.toUpperCase(), holder.shareholding_percentage, holder.no_of_shares));
          }
        }
    
        if (rows.length === 1) {
          rows.push(
            new TableRow({
              children: [
                new TableCell({
                  columnSpan: 4,
                  verticalAlign: VerticalAlign.CENTER, // optional: vertical center
                  children: [
                    new Paragraph({
                      alignment: AlignmentType.CENTER, // horizontal center
                      children: [
                        new TextRun({
                          text: "No shareholding above 5% found for this company.",
                          size : 20,
                        }),
                      ],
                      
                    }),
                  ],
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
              ],
            })
          );
        }
    
        // Add year label paragraph
  
    
        // Add table
        section.push(
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
            },
            rows,
          })
        );
      }
    
      return section;
    }

    const headerParagraph = new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 600, after: 200 },
      children: [
        new TextRun({
          text: "Debt Profile",
          bold: true,
          size: 28,
          font: "Montserrat",
        }),
        new TextRun({
          text: `( As on ${formattedDate} )`, // two spaces for visual separation
          size: 24,
          font: "Montserrat",
        }),
      ],
    });

    const headershareholdingParagraph = new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 600, after: 200 },
      children: [
        new TextRun({
          text: "Shareholding More Than 5%",
          bold: true,
          size: 28,
          font: "Montserrat",
        }),
        ...(formattedDateshareholding
          ? [
              new TextRun({
                text: `  As on ${formattedDateshareholding}`,
                size: 24,
                font: "Montserrat",
              }),
            ]
          : []),
      ],
    });

    const headerContributionDetailsParagraph = new Paragraph({
      alignment: AlignmentType.LEFT,
      spacing: { before: 600, after: 200 },
      children: [
        new TextRun({
          text: "LLP Contribution Details",
          bold: true,
          size: 28,
          font: "Montserrat",
        }),
      ],
    });

    function createContributionDetailsSection(
      contributionDetails: any
    ): (Paragraph | Table)[] {
      const section: (Paragraph | Table)[] = [];
    
      // Check if data exists
      if (!contributionDetails) {
        return [];
      }

      // Normalize to array - handle both single object and array
      const detailsArray = Array.isArray(contributionDetails) 
        ? contributionDetails 
        : [contributionDetails];
    
      if (detailsArray.length === 0) {
        return [];
      }

      // Check if any year has actual data
      const hasData = detailsArray.some((yearData: any) => {
        const hasIndividualPartners = yearData.individual_partners && yearData.individual_partners.length > 0;
        const hasBodyCorporates = yearData.body_corporates && yearData.body_corporates.length > 0;
        return hasIndividualPartners || hasBodyCorporates;
      });

      // If no data exists, show "no data" message
      if (!hasData) {
        section.push(headerContributionDetailsParagraph);
        section.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 200 },
            children: [
              new TextRun({
                text: "No contribution details available for this LLP.",
                size: 20,
              }),
            ],
          })
        );
        return section;
      }
    
      // Section Heading
      section.push(headerContributionDetailsParagraph);
    
      // Iterate over each year block
      for (const yearData of detailsArray) {
        const hasIndividualPartners = yearData.individual_partners && yearData.individual_partners.length > 0;
        const hasBodyCorporates = yearData.body_corporates && yearData.body_corporates.length > 0;
        const hasYearData = hasIndividualPartners || hasBodyCorporates;

        // Year header
        section.push(
          new Paragraph({
            spacing: { before: 400, after: 200 },
            children: [
              new TextRun({
                text: `Financial Year: ${yearData.financial_year}`,
                bold: true,
                size: 24,
              }),
            ],
          })
        );

        if (!hasYearData) {
          section.push(
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 200, after: 200 },
              children: [
                new TextRun({
                  text: "No contribution data available for this financial year.",
                  size: 20,
                }),
              ],
            })
          );
          continue;
        }

        // Individual Partners Table
        if (hasIndividualPartners) {
          section.push(
            new Paragraph({
              spacing: { before: 200, after: 100 },
              children: [
                new TextRun({
                  text: "Individual Partners",
                  bold: true,
                  size: 22,
                }),
              ],
            })
          );

          const individualRows: TableRow[] = [
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Name", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "DIN", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Designation", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Obligation Contribution", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Received Contribution", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
              ],
            }),
          ];

          for (const partner of yearData.individual_partners) {
            individualRows.push(
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: partner.name || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: partner.id || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: partner.designation || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ 
                        text: `₹${(partner.obligation_contribution || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, 
                        size: 18 
                      })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ 
                        text: `₹${(partner.received_contribution || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, 
                        size: 18 
                      })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                ],
              })
            );
          }

          section.push(
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              },
              rows: individualRows,
            })
          );
        }

        // Body Corporates Table
        if (hasBodyCorporates) {
          section.push(
            new Paragraph({
              spacing: { before: 400, after: 100 },
              children: [
                new TextRun({
                  text: "Body Corporates",
                  bold: true,
                  size: 22,
                }),
              ],
            })
          );

          const bodyCorporateRows: TableRow[] = [
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Name", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "CIN", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Nominee Name", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "DIN", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Obligation Contribution", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
                new TableCell({
                  children: [new Paragraph({
                    children: [new TextRun({ text: "Received Contribution", bold: true, size: 20 })],
                  })],
                  shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto"},
                  margins: { top: 150, bottom: 150, left: 150, right: 150 },
                }),
              ],
            }),
          ];

          for (const corporate of yearData.body_corporates) {
            bodyCorporateRows.push(
              new TableRow({
                children: [
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: corporate.name || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: corporate.id || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: corporate.nominee_name || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ text: corporate.nominee_id || "—", size: 18 })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ 
                        text: `₹${(corporate.obligation_contribution || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, 
                        size: 18 
                      })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      children: [new TextRun({ 
                        text: `₹${(corporate.received_contribution || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, 
                        size: 18 
                      })],
                    })],
                    margins: { top: 150, bottom: 150, left: 150, right: 150 },
                  }),
                ],
              })
            );
          }

          section.push(
            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "E5E7EB" },
              },
              rows: bodyCorporateRows,
            })
          );
        }
      }
    
      return section;
    }

    // Conditionally create sections based on data availability
    const shareholdingSection = data?.shareHoldingMoreThenFivePercentSession !== undefined 
      ? createShareholdingSection(data.shareHoldingMoreThenFivePercentSession || [])
      : [];
    
    const contributionDetailsSection = data?.shareHoldingMoreThenFivePercentSession === undefined && data?.contribution_details !== undefined
      ? createContributionDetailsSection(data.contribution_details)
      : [];
    
    function formatDate(dateStr?: string): string {
      if (!dateStr) return "—";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric"
        });
      } catch {
        return "—";
      }
    }

    let relatedPartyTransactions = {
      holding_entities:data?.relatedPartySection.holding_entities,
      joint_ventures:data?.relatedPartySection.joint_ventures,
      subsidiary_entities:data?.relatedPartySection.subsidiary_entities,
      associate_entities:data?.relatedPartySection.associate_entities
    }

    function createRelatedPartyTables(data: any) {
      const sections = [
        { title: "Holding Corporates", group: data.holding_entities },
        { title: "Subsidiary Corporates", group: data.subsidiary_entities },
        { title: "Associate Corporates", group: data.associate_entities },
        { title: "Joint Ventures", group: data.joint_ventures },
      ];

      const allEmpty = sections.every(
        ({ group }) =>
          !group ||
          (
            (!group.company || group.company.length === 0) &&
            (!group.llp || group.llp.length === 0) &&
            (!group.others || group.others.length === 0)
          )
      );

      if (allEmpty) {
        return [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: "This company has no related party",
                bold: true,
                size: 20, // reduced from 24
                color: "000000", // black
              }),
            ],
            spacing: { before: 600, after: 200 },
          }),
        ];
      }

      const getStatusBadge = (status: string | undefined) => {
        return status || "—";
      };
    
      return sections.flatMap(({ title, group }) => {
        if (!group || (
          group.company.length === 0 &&
          group.llp.length === 0 &&
          group.others.length === 0
        )) {
          return [];
        }
    
        const allEntities: any[] = [
          ...group.company.map((e:any) => ({ ...e, entityType: "Company" })),
          ...group.llp.map((e:any) => ({ ...e, entityType: "LLP" })),
          ...group.others.map((e:any) => ({ ...e, entityType: "Others" })),
        ];
    
        return [
          new Paragraph({
            spacing: { before: 600, after: 200 },
            children: [
              new TextRun({
                text: title,
                bold: true,
                size: 28,
                color: "000000",
              }),
            ],
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            },
            rows: [
              // Header row
              new TableRow({
                children: [
                  ...[
                    "Corporate Name",
                    "Type",
                    "Shareholding (%)",
                    "City",
                    "DOI",
                    "Status",
                  ].map((text) =>
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER, // ✅ vertical center
                      shading: {
                        fill: "f8f6f1",
                        type: ShadingType.CLEAR,
                        color: "auto",
                      },
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          spacing: { before: 100, after: 100 }, // ✅ top/bottom spacing
                          alignment: AlignmentType.LEFT,
                          children: [
                            new TextRun({ text, bold: true, size: 20 }),
                          ],
                        }),
                      ],
                    })
                  ),
                ],
              }),
    
              // Entity rows
              ...allEntities.map((entity) =>
                new TableRow({
                  children: [
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER,
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: entity.legal_name || "N/A",
                              bold: true,
                              size: 20,
                            }),
                            ...(entity.cin
                              ? [
                                  new TextRun({
                                    text: `\nCIN: ${entity.cin}`,
                                    size: 18,
                                    color: "666666",
                                  }),
                                ]
                              : []),
                            ...(entity.llpin
                              ? [
                                  new TextRun({
                                    text: `\nLLPIN: ${entity.llpin}`,
                                    size: 18,
                                    color: "666666",
                                  }),
                                ]
                              : []),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER,
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({ text: entity.entityType, size: 20 }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER,
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text:
                                typeof entity.share_holding_percentage === "number"
                                  ? `${entity.share_holding_percentage}%`
                                  : "—",
                              size: 20,
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER,
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: entity.city || "—",
                              size: 20,
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER,
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({
                              text: formatDate(entity.incorporation_date),
                              size: 20,
                            }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      verticalAlign: VerticalAlign.CENTER,
                      margins: {
                        top: 100,
                        bottom: 100,
                        left: 100,
                        right: 100,
                      },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.CENTER,
                          children: [
                            new TextRun({
                              text: getStatusBadge(entity.status),
                              size: 20,
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                })
              ),
            ],
          }),
        ];
      });
    }

    const relatedPartyEntityTables = createRelatedPartyTables(relatedPartyTransactions); // from earlier


    function createRelatedPartyTransactionTables(transactions: any[]) {
      // Check if transactions is empty or all years have no data
      const hasAnyData = Array.isArray(transactions) && transactions.some(yearData => {
        if (!yearData) return false;
        const all = [
          ...(yearData.company || []),
          ...(yearData.llp || []),
          ...(yearData.individual || []),
          ...(yearData.others || [])
        ];
        return all.length > 0;
      });

      if (!hasAnyData) {
        return [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                text: "This company has no reported related party transactions.",
                bold: true,
                size: 20,
                color: "000000",
              }),
            ],
            spacing: { before: 600, after: 200 },
          }),
        ];
      }

      const recentTransactions = [...transactions]
        .sort((a, b) => parseInt(b.financial_year) - parseInt(a.financial_year))
        .slice(0, 3);
    
      return recentTransactions.flatMap((yearData: any) => {
        const all = [...yearData.company, ...yearData.llp, ...yearData.individual, ...yearData.others];
    
        const grouped = all.reduce((acc: any, txn: any) => {
          const key = txn.cin || txn.din || txn.name;
          const rawAmount = txn.amount;
          const amount = typeof rawAmount === "number" ? rawAmount : 0;
    
          if (!acc[key]) {
            acc[key] = {
              name: txn.name,
              legal_name: txn.legal_name,
              id: txn.cin || txn.din || txn.name,
              relationship: txn.relationship,
              revenue: 0,
              expense: 0,
            };
          }
    
          if (txn.type_of_transaction === "Revenue") {
            acc[key].revenue += amount;
          } else {
            acc[key].expense += amount;
          }
    
          return acc;
        }, {});
    
        const entities = Object.values(grouped).filter(
          (e: any) => e.revenue !== 0 || e.expense !== 0
        );
    
        if (entities.length === 0) return [];
    
        const totalRevenue = entities.reduce((sum: number, e: any) => sum + e.revenue, 0);
        const totalExpense = entities.reduce((sum: number, e: any) => sum + e.expense, 0);
        const netAmount = totalRevenue - totalExpense;
    
        return [
          new Paragraph({
            children: [
              new TextRun({
                text: `FY ${yearData.financial_year.slice(0, 4)}`,
                bold: true,
                size: 28,
                color: "000000",
              }),
            ],
            spacing: { before: 600, after: 200 },
          }),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
            },
            rows: [
              // Header Row
              new TableRow({
                children: [
                  ...["Name", "Relationship", "Revenue", "Expense", "Net Amount"].map(header =>
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: header, bold: true, size: 20 })] })],
                      shading: { fill: "f8f6f1", type: ShadingType.CLEAR, color: "auto" },
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                    })
                  ),
                ],
              }),
    
              // Entity Rows
              ...entities.map((e: any) => {
                const net = e.revenue - e.expense;
                return new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({
                        children: [
                          new TextRun({ text: e.name, bold: true, size: 20 }),
                          new TextRun({ text: `\n${e.id}`, size: 18, break: 1, color: "666666" }),
                        ],
                      })],
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                    }),
                    new TableCell({
                      children: [new Paragraph({ children: [new TextRun({ text: e.relationship || "—", size: 20 })] })],
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [
                        new TextRun({ text: e.revenue > 0 ? formatCurrency(e.revenue) : "—", size: 20 })
                      ]})],
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                    }),
                    new TableCell({
                      children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [
                        new TextRun({ text: e.expense > 0 ? formatCurrency(e.expense) : "—", size: 20 })
                      ]})],
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                    }),
                    new TableCell({
                      children: [new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                          new TextRun({
                            text: formatCurrency(Math.abs(net)) + (net < 0 ? " (net expense)" : ""),
                            size: 20,
                            color: net < 0 ? "B91C1C" : "15803D", // red or green
                            bold: true,
                          }),
                        ],
                      })],
                      margins: { top: 150, bottom: 150, left: 150, right: 150 },
                    }),
                  ],
                });
              }),
    
              // Total Row
              new TableRow({
                children: [
                  new TableCell({
                    verticalAlign: VerticalAlign.CENTER,
                    children: [new Paragraph({
                    alignment: AlignmentType.CENTER,
                      children: [new TextRun({ text: "Total", bold: true, size: 20 })],
                    })],
                    shading: { fill: "eff6ff", type: ShadingType.CLEAR, color: "auto" },
                  }),
                  new TableCell({ children: [] }),
                  new TableCell({
                    children: [new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new TextRun({ text: formatCurrency(totalRevenue), size: 20, bold: true, color: "15803D" }),
                      ],
                    })],
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new TextRun({ text: formatCurrency(totalExpense), size: 20, bold: true, color: "B91C1C" }),
                      ],
                    })],
                  }),
                  new TableCell({
                    children: [new Paragraph({
                      alignment: AlignmentType.CENTER,
                      children: [
                        new TextRun({
                          text: formatCurrency(Math.abs(netAmount)) + (netAmount < 0 ? " (net expense)" : ""),
                          bold: true,
                          size: 20,
                          color: netAmount < 0 ? "B91C1C" : "15803D",
                        }),
                      ],
                    })],
                  }),
                ],
              })
            ]
          })
        ];
      });
    }
    
    const doc = new Document({
      styles: {
        default: {
          document: {
            run: {
              font: "Montserrat",           // Set default font
              size : 20,                     // Optional: default size (22 = 11pt)
            }
          },
        },
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 720,
                right: 720,
                bottom: 720,
                left: 720,
              },
            },

          },
          children: [

            logoImage,
            // Header with professional styling
            new Paragraph({
              children: [
                new TextRun({
                  text: data?.company_details?.legal_name || "N/A",
                  size: 24,
                  bold: true,
                  color: "1F2937",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 300 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Report Date: ${new Date().toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}`,
                  size: 20,
                  color: "374151",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 600 },
            }),

            // Company Profile Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Company Profile",
                  size: 28,
                  bold: true,
                  color: "000000",
                }),
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 300 },
            }),

            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              },
              rows: company_details_row,
            }),

            // Business Description
            new Paragraph({
              children: [
                new TextRun({
                  text: "Business Description",
                  size: 24,
                  bold: true,
                  color: "000000",
                }),
              ],
              spacing: { before: 400, after: 200 },
            }),

            new Table({
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                left: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                right: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
                insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "D1D5DB" },
              },
              rows: business_details_table_rows,
            }),

            // Open Charges Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Open Charges",
                  size: 28,
                  bold: true,
                  color: "000000",
                }),
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 600, after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: `Total Charges: ${formatCurrency(totalAmount)}`,
                  bold: true,
                  size : 20,
                  color: "000000",
                }),
              ],
              spacing: { after: 300 },
            }),
            openChargesTable,

            // Director Details Section
            new Paragraph({
              children: [
                new TextRun({
                  text: "Key Management People",
                  size: 28,
                  bold: true,
                  color: "000000",
                }),
              ],
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 600, after: 200 },
            }),
            directorTable,

            ...keyRatiosTables,
            ...gstDetailsSection,
            ...shareholdingSection,
            ...contributionDetailsSection,
            headerParagraph,
            debtProfileTable,

            headerSecuritiesAllotmentParagraph,
            securitiesallotmentTable,
            headerRelatedPartyParagraph,
            ...relatedPartyEntityTables,
            
            headerRelatedPartyTranstionParagraph,
            ...createRelatedPartyTransactionTables(data.relatedPartySection.related_party_transactions),

            // Compliance Overview Section
           
            // Professional Footer
            new Paragraph({
              children: [
                new TextRun({
                  text: "Disclaimer",
                  size: 24,
                  bold: true,
                  color: "1F2937",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 600, after: 200 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "This report is auto-generated from available data. Please verify all information independently before making financial decisions.",
                  size: 20,
                  color: "6B7280",
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { after: 300 },
            }),

            new Paragraph({
              children: [
                new TextRun({
                  text: "© 2024 Astrafin Neosolutions Private Limited.",
                  size: 18,
                  color: "9CA3AF",
                  italics: true,
                }),
              ],
              alignment: AlignmentType.CENTER,
              spacing: { before: 200 },
            }),
          ],
        },
      ],
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'company-financial-report.docx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return { generateDocx };
};
